import { Grid, Paper, CircularProgress, Typography, Divider, Box } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getMemberById } from 'src/shared/services/memberService';
import { useTranslation } from 'react-i18next';
import { Email, Phone } from '@mui/icons-material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { Member } from '../models/member';
import MemberAvatar from '../components/memberAvatar';
import { MembershipPartial } from '../components/membershipPartial';
import { MemberDetailGender } from '../components/memberDetailGender';
import { MemberDetailBirthDate } from '../components/memberDetailBirthDate';
import { MemberStatuses } from '../components/memberStatuses';
import { MemberDetail } from '../components/memberDetail';
import { MemberDetailLink } from '../components/memberDetailLink';
import { MemberUpdateRequestedMessage } from '../components/memberUpdateRequestedMessage';
import MemberInvoices from '../components/memberInvoices';
import MemberInvoiceAddress from '../components/memberInvoiceAddress';
import { MemberTitleSummary } from '../components/memberTitleSummary';
import { MemberDetailsActionButtons } from '../components/memberDetailsActionButtons';
import { MemberDetailProfileGroupSummary } from '../components/memberDetailProfileGroupSummary';
import { EmailSubscriptionHandler } from '../components/emailSubscriptionHandler';

type MemberDetailsPageProps = {
  otherContactMode: boolean;
};

export const MemberDetailsPage = (props: MemberDetailsPageProps) => {
  const { otherContactMode } = props;
  const { t } = useTranslation(['member', 'common']);
  const isMounted = useIsMounted();
  const { id } = useParams();
  const { dateStamp, currentUser } = useGlobals();
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label'));

  const [loading, setIsLoading] = useState(false);
  const [member, setMember] = useState<Member>();
  const [timeStamp, setTimeStamp] = useState<string>(new Date().getUTCMilliseconds().toString());

  const fetchData = useCallback(
    async (uid: string | number) => {
      setIsLoading(true);
      try {
        const result = await getMemberById(uid);
        if (isMounted()) {
          setMember(result.data);
          ConsoleHelper.log('RES member', result.data, dateStamp);
        }
      } catch (err) {
        if (isMounted()) {
          ConsoleHelper.log(err);
        }
      } finally {
        if (isMounted()) {
          setIsLoading(false);
        }
      }
    },
    [isMounted, dateStamp],
  );

  useEffect(() => {
    if (id !== undefined) fetchData(id);
  }, [id, fetchData, timeStamp]);

  const displayDetails = (label: string, value: string | undefined, key: string | number, fullWidth = true) => {
    return (
      value && (
        <Grid item xs={fullWidth ? 'auto' : 6} zeroMinWidth sx={{ maxWidth: fullWidth ? '100%' : '50%' }} key={key}>
          <Typography variant='body1' component='div' sx={{ fontWeight: 500 }}>
            {label.replace(/<BR>/gi, '')}
          </Typography>
          <Typography variant='body1' component='div'>
            {value}
          </Typography>
        </Grid>
      )
    );
  };

  const handleMemberUpdate = () => {
    setTimeStamp(new Date().getUTCMilliseconds().toString()); // Force a reload
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs sx={{ display: 'flex', alignItems: 'center' }}>
          <MainContentHeader
            isLoading={loading}
            title={otherContactMode ? otherLabel : t('member:memberDetails.header', { memberNr: member?.memberNr })}
          />
          {member && (
            <Box sx={{ ml: 1 }}>
              <MemberStatuses status={member.status} resDate={member.resDate} chipSize='small' />
            </Box>
          )}
        </Grid>
        {!loading && member && (
          <Grid item xs='auto' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <MemberDetailsActionButtons otherContactMode={otherContactMode} member={member} fetchData={fetchData} />
          </Grid>
        )}
        {!loading && member && member.memberUpdate_id !== null && (
          <MemberUpdateRequestedMessage isMember={false} memberUpdateId={member.memberUpdate_id} callback={() => handleMemberUpdate()} />
        )}
        <Grid item xs={10}>
          <Paper sx={{ p: 3, overflow: 'hidden' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
              </Box>
            )}
            {!loading && member && (
              <GridWrapper>
                <Grid item xs={12} lg={8} pr='24px'>
                  <GridWrapper>
                    <Grid item xs={12}>
                      <ContentSection>
                        <GridWrapper>
                          <Grid item xs='auto'>
                            <MemberAvatar firstName={member.firstName ?? ''} lastName={member.lastName ?? ''} />
                          </Grid>
                          <Grid item xs='auto'>
                            <Typography variant='h6' sx={{ fontWeight: 700 }} gutterBottom>{`${member.firstName} ${member.lastName}`}</Typography>
                            <MemberDetail detail={member.address} />
                            <MemberDetail detail={member.addressOther} />
                            <MemberDetail detail={member.coAddress} />
                            <MemberDetail detail={member.postNr} />
                            <MemberDetail detail={member.postAddress} />
                          </Grid>
                          <Grid item mt={-1}>
                            <Typography variant='h6' gutterBottom>
                              &nbsp;
                            </Typography>
                            <MemberDetailLink
                              icon={<Email sx={{ mr: 1 }} />}
                              linkPrefix='mailto:'
                              details={[member.email || '']}
                              endIcon={<EmailSubscriptionHandler email={member.email} />}
                            />
                            <MemberDetailLink
                              icon={<Phone sx={{ mr: 1 }} />}
                              linkPrefix='tel:'
                              details={[member.phone || '', member.phoneOther || '']}
                            />
                            <MemberDetailBirthDate birthDate={member.birthDate} />
                            <MemberDetailGender sex={member.sex} />
                          </Grid>
                        </GridWrapper>
                        {((member.customData && member.customData?.filter((x) => !!x.value).length > 0) ||
                          (member.profileData &&
                            member.profileData?.filter((x) => x.locked && (x.textValue === null || (x.textValue !== null && x.textValue !== '')))
                              .length > 0)) && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <GridWrapper>
                              {member.customData?.filter((x) => !!x.value).map((item) => displayDetails(item.heading, item.value, item.key))}
                              <MemberDetailProfileGroupSummary
                                profileData={member.profileData
                                  ?.filter((x) => x.locked && (x.textValue === null || (x.textValue !== null && x.textValue !== '')))
                                  .sort((a, b) => a.pos - b.pos)}
                                maxItemsShown={3}
                              />
                            </GridWrapper>
                          </>
                        )}
                        {member.profileData &&
                          member.profileData?.filter((x) => !x.locked && (x.textValue === null || (x.textValue !== null && x.textValue !== '')))
                            .length > 0 && (
                            <>
                              <Divider sx={{ my: 3 }} />
                              <GridWrapper>
                                <MemberDetailProfileGroupSummary
                                  profileData={member.profileData?.filter((x) => !x.locked).sort((a, b) => a.pos - b.pos)}
                                  maxItemsShown={3}
                                />
                              </GridWrapper>
                            </>
                          )}
                        {(member.comment || member.note) && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <GridWrapper>
                              {displayDetails(t('member:memberDetails.comment'), member.comment, 'comment', false)}
                              {displayDetails(t('member:memberDetails.note'), member.note, 'note', false)}
                            </GridWrapper>
                          </>
                        )}
                      </ContentSection>
                    </Grid>
                    {member && (
                      <Grid item xs={12}>
                        <MemberInvoiceAddress member={member} />
                      </Grid>
                    )}
                    {member && (
                      <Grid item xs={12} mb={{ xs: 2, lg: 0 }}>
                        <MemberInvoices otherContactMode={otherContactMode} member={member} />
                      </Grid>
                    )}
                    {member && member.titles && member.titles.length > 0 && (
                      <Grid item xs={12}>
                        <MemberTitleSummary member={member} />
                      </Grid>
                    )}
                  </GridWrapper>
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={4}
                  sx={(theme) => ({
                    borderLeft: {
                      xs: 'none',
                      lg: `1px solid ${theme.palette.divider}`,
                    },
                  })}
                >
                  {member && (
                    <MembershipPartial otherContactMode={otherContactMode} member={member} refetchMemberCallback={() => id && fetchData(id)} />
                  )}
                </Grid>
              </GridWrapper>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
    </>
  );
};
